import { useSetState } from "react-use";
import { createContext, useContext, useMemo } from "react";

/**
 * Represents the state of an application with configuration for a guided tour.
 *
 * @property {boolean} run - Indicates if the application is in a "running" state.
 * @property {number} stepIndex - The current step index in the guided tour.
 * @property {Array} steps - An array representing the steps in the tour.
 * @property {boolean} tourActive - Indicates whether the guided tour is currently active.
 */
const workshopUserTourState = {
  run: false,
  stepIndex: 0,
  steps: [],
  tourActive: false,
};

/**
 * Context object for managing the state of the workshop user tour.
 * This context provides a shared state and a method to update the state
 * across components related to the workshop user tour feature.
 *
 * The `state` represents the current state of the user tour, initializing
 * with the `workshopUserTourState` object.
 * The `setState` function is provided as a placeholder function and
 * should be overridden with an actual state updating function.
 */
const WorkshopUserTourContext = createContext({
  state: workshopUserTourState,
  setState: () => undefined,
});

/**
 * Provides the context for the Workshop User Tour.
 * It manages the state related to the workshop user tour and supplies
 * the context value to its descendants.
 *
 * @param {Object} props - The properties passed to the provider component.
 * @return {JSX.Element} The Workshop User Tour context provider component.
 */
export function WorkshopUserTourProvider(props) {
  const [state, setState] = useSetState(workshopUserTourState);

  const value = useMemo(
    () => ({
      state,
      setState,
    }),
    [setState, state],
  );

  return <WorkshopUserTourContext.Provider value={value} {...props} />;
}

/**
 * Custom hook to access the WorkshopUserTourContext.
 * Ensures that the hook is used within a valid context provider.
 *
 * @return {Object} The value provided by the WorkshopUserTourContext.
 * @throws {Error} If the hook is used outside of a valid WorkshopUserTourContext provider.
 */
export function useWorkshopTourContext() {
  const context = useContext(WorkshopUserTourContext);

  if (!context) {
    throw new Error("useAppContext must be used within a AppProvider");
  }

  return context;
}
